import { useMemo, useState } from "react";
import Reveal from "./Reveal";

/**
 * Simple homeowner savings estimate. Transparent assumptions, no invented utility
 * data: usage = bill ÷ $0.15/kWh, array sized to offset ~90% of usage at
 * 130 kWh/kW-year Florida average, installed at $2.30–$3.00 per watt.
 */
const RATE = 0.15;
const OFFSET = 0.9;
const PRODUCTION = 130;
const COST_PER_WATT = [2.3, 3.0];

export default function HomeownerCalculator({ onQuote }) {
  const [bill, setBill] = useState("");
  const [zip, setZip] = useState("");
  const [show, setShow] = useState(false);

  const result = useMemo(() => {
    const b = Number(bill);
    if (!b || b <= 0) return null;
    const monthlyKwh = b / RATE;
    const kw = (monthlyKwh * OFFSET) / PRODUCTION;
    const monthlySavings = b * OFFSET;
    return {
      kw: Math.round(kw * 10) / 10,
      costLow: Math.round(kw * 1000 * COST_PER_WATT[0] / 500) * 500,
      costHigh: Math.round(kw * 1000 * COST_PER_WATT[1] / 500) * 500,
      monthlySavings: Math.round(monthlySavings),
      yearlySavings: Math.round(monthlySavings * 12),
    };
  }, [bill]);

  function estimate(e) {
    e.preventDefault();
    setShow(Boolean(result));
  }

  return (
    <section className="section" style={{ paddingTop: 44, paddingBottom: 10 }} data-testid="homeowner-calculator">
      <div className="wrap">
        <div className="section-heading">
          <div>
            <span className="eyebrow">HOME SAVINGS ESTIMATE</span>
            <h2>Run your own numbers first.</h2>
          </div>
          <p>A thirty-second ballpark from your electric bill — the free quote confirms real numbers for your roof.</p>
        </div>
        <Reveal>
          <form className="panel" style={{ maxWidth: 560 }} onSubmit={estimate} data-testid="calculator-form">
            <div className="twofields">
              <label className="field">
                Average monthly electric bill ($)
                <input required type="number" min={30} max={5000} data-testid="calc-bill"
                  placeholder="e.g. 220" value={bill} onChange={(e) => setBill(e.target.value)} />
              </label>
              <label className="field">
                ZIP code
                <input required type="text" inputMode="numeric" maxLength={5} data-testid="calc-zip"
                  placeholder="e.g. 33913" value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} />
              </label>
            </div>
            <button className="btn lime" type="submit" data-testid="calc-estimate">Estimate my savings</button>
          </form>
        </Reveal>
        {show && result && (
          <Reveal>
            <div className="panel" style={{ maxWidth: 560, marginTop: 16 }} data-testid="calc-results">
              <div className="journey" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="journey-step"><span className="num">1</span><b>Estimated system size</b><p data-testid="calc-kw">≈ {result.kw} kW</p></div>
                <div className="journey-step"><span className="num">2</span><b>Estimated installed cost</b><p data-testid="calc-cost">${result.costLow.toLocaleString()} – ${result.costHigh.toLocaleString()}</p></div>
                <div className="journey-step"><span className="num">3</span><b>Estimated monthly savings</b><p data-testid="calc-monthly">≈ ${result.monthlySavings.toLocaleString()}/mo</p></div>
                <div className="journey-step"><span className="num">4</span><b>Estimated yearly savings</b><p data-testid="calc-yearly">≈ ${result.yearlySavings.toLocaleString()}/yr</p></div>
              </div>
              <p className="micro" style={{ marginTop: 12 }}>Estimate only — your free quote confirms real numbers. Assumes $0.15/kWh, ~90% offset and $2.30–$3.00 per watt installed; every roof and utility is different.</p>
              <button className="btn" data-testid="calc-get-quote" onClick={() => onQuote(Number(bill))}>Get my exact quote <span>↗</span></button>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
